import type {GridFilterItem, GridFilterModel} from '@mui/x-data-grid';

//TYPES
type FilterColumnKind = 'text' | 'status';

const FILTER_PARAM_PREFIX = 'f_';
const OPERATOR_SEPARATOR = ':';
const MULTI_VALUE_SEPARATOR = '|';

const FILTERABLE_COLUMNS = new Map<string, FilterColumnKind>([
  ['createdAt', 'text'],
  ['code', 'text'],
  ['concello', 'text'],
  ['appraiserName', 'text'],
  ['plannedCuttingDate', 'text'],
  ['finsaStatus', 'status'],
  ['aseforStatus', 'status'],
  ['fortopStatus', 'status'],
]);

const TEXT_OPERATORS = [
  'contains',
  'doesNotContain',
  'equals',
  'doesNotEqual',
  'startsWith',
  'endsWith',
  'isEmpty',
  'isNotEmpty',
  'isAnyOf',
];

const STATUS_OPERATORS = ['is', 'not', 'isAnyOf'];

const OPERATORS_BY_KIND: Record<FilterColumnKind, string[]> = {
  text: TEXT_OPERATORS,
  status: STATUS_OPERATORS,
};

const OPERATORS_WITHOUT_VALUE = ['isEmpty', 'isNotEmpty'];
const MULTI_VALUE_OPERATORS = ['isAnyOf'];

export const parseFilterModel = (searchParams: URLSearchParams): GridFilterModel => {
  const items: GridFilterItem[] = [];
  const occurrences = new Map<string, number>();

  searchParams.forEach((rawParamValue, paramName) => {
    if (!paramName.startsWith(FILTER_PARAM_PREFIX)) return;

    const field = paramName.slice(FILTER_PARAM_PREFIX.length);
    const kind = FILTERABLE_COLUMNS.get(field);
    if (!kind) return;

    const separatorIndex = rawParamValue.indexOf(OPERATOR_SEPARATOR);
    if (separatorIndex < 0) return;

    const operator = rawParamValue.slice(0, separatorIndex);
    if (!OPERATORS_BY_KIND[kind].includes(operator)) return;

    //El id se deriva de la columna y de su posición, nunca del valor: así el id no cambia
    //mientras el usuario escribe y el formulario de filtro no se vuelve a montar.
    const occurrence = occurrences.get(field) ?? 0;
    occurrences.set(field, occurrence + 1);

    const item: GridFilterItem = {
      id: `${field}-${occurrence}`,
      field: field,
      operator: operator,
    };

    if (!OPERATORS_WITHOUT_VALUE.includes(operator)) {
      const rawValue = rawParamValue.slice(separatorIndex + 1);
      item.value = MULTI_VALUE_OPERATORS.includes(operator)
        ? rawValue.split(MULTI_VALUE_SEPARATOR).filter((value) => value !== '')
        : rawValue;
    }

    items.push(item);
  });

  return {items: items};
};

/**
 * Escribe en los parámetros de la URL los filtros del modelo, conservando el resto de
 * parámetros (por ejemplo el orden de las columnas).
 */
export const applyFilterModelToSearchParams = (
  searchParams: URLSearchParams,
  filterModel: GridFilterModel,
): URLSearchParams => {
  const nextSearchParams = new URLSearchParams(searchParams);

  for (const paramName of [...nextSearchParams.keys()]) {
    if (paramName.startsWith(FILTER_PARAM_PREFIX)) {
      nextSearchParams.delete(paramName);
    }
  }

  for (const item of filterModel.items) {
    const kind = FILTERABLE_COLUMNS.get(item.field);
    if (!kind) continue;
    if (!OPERATORS_BY_KIND[kind].includes(item.operator)) continue;

    const serializedValue = serializeFilterValue(item);
    if (serializedValue === undefined) continue;

    nextSearchParams.append(
      `${FILTER_PARAM_PREFIX}${item.field}`,
      `${item.operator}${OPERATOR_SEPARATOR}${serializedValue}`,
    );
  }

  return nextSearchParams;
};

const serializeFilterValue = (item: GridFilterItem): string | undefined => {
  if (OPERATORS_WITHOUT_VALUE.includes(item.operator)) return '';
  if (item.value == null) return '';
  if (Array.isArray(item.value)) return item.value.map((value) => String(value)).join(MULTI_VALUE_SEPARATOR);
  return String(item.value);
};
